import React, { useState, useEffect, useRef } from 'react';
import { RefreshCw, CheckCircle2, AlertCircle, Folder, FileText, Clock, X } from 'lucide-react';
import type { SyncProgressEvent } from '../../server/services/driveSync';

interface SyncProgressModalProps {
  isOpen: boolean;
  spaceSlug: string;
  spaceName: string;
  targetFolderId?: string | null;
  targetFolderName?: string;
  apiUrl: string;
  onClose: () => void;
  onSyncComplete: () => void;
}

export const SyncProgressModal: React.FC<SyncProgressModalProps> = ({
  isOpen,
  spaceSlug,
  spaceName,
  targetFolderId,
  targetFolderName,
  apiUrl,
  onClose,
  onSyncComplete,
}) => {
  const [status, setStatus] = useState<'idle' | 'syncing' | 'complete' | 'error'>('idle');
  const [currentFolderName, setCurrentFolderName] = useState<string>(targetFolderName || spaceName);
  const [itemsSynced, setItemsSynced] = useState<number>(0);
  const [foldersTraversed, setFoldersTraversed] = useState<number>(0);
  const [deletedStaleRows, setDeletedStaleRows] = useState<number>(0);
  const [durationMs, setDurationMs] = useState<number>(0);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState<number>(0);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!isOpen) {
      // Clean up when modal closes
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setStatus('idle');
      setItemsSynced(0);
      setFoldersTraversed(0);
      setDeletedStaleRows(0);
      setDurationMs(0);
      setErrorMsg(null);
      setElapsedSeconds(0);
      return;
    }

    // Start sync connection
    setStatus('syncing');
    setElapsedSeconds(0);
    setCurrentFolderName(targetFolderName || spaceName);

    // Live elapsed timer
    const startTime = Date.now();
    timerRef.current = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    let streamUrl = `${apiUrl}/api/admin/spaces/${spaceSlug}/sync/stream`;
    if (targetFolderId) {
      streamUrl += `?folderId=${encodeURIComponent(targetFolderId)}`;
    }

    const es = new EventSource(streamUrl, { withCredentials: true });
    eventSourceRef.current = es;

    es.onmessage = (event) => {
      if (event.data === '[DONE]') {
        es.close();
        return;
      }

      try {
        const payload: SyncProgressEvent = JSON.parse(event.data);

        if (payload.type === 'progress') {
          if (payload.currentFolderName) {
            setCurrentFolderName(payload.currentFolderName);
          }
          setItemsSynced(payload.itemsSynced || 0);
          setFoldersTraversed(payload.foldersTraversed || 0);
        } else if (payload.type === 'complete') {
          setStatus('complete');
          setItemsSynced(payload.itemsSynced || 0);
          setFoldersTraversed(payload.foldersTraversed || 0);
          setDeletedStaleRows(payload.deletedStaleRows || 0);
          setDurationMs(payload.durationMs || 0);
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          es.close();
        } else if (payload.type === 'error') {
          setStatus('error');
          setErrorMsg(payload.error || 'Synchronization failed');
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          es.close();
        }
      } catch (err) {
        console.error('Error parsing SSE payload:', err);
      }
    };

    es.onerror = (err) => {
      console.error('SSE connection error:', err);
      es.close();
      // Fallback: If SSE stream breaks or completes without payload, handle state
      if (status === 'syncing') {
        setStatus('complete');
        if (timerRef.current) clearInterval(timerRef.current);
      }
    };

    return () => {
      es.close();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isOpen, spaceSlug, targetFolderId]);

  if (!isOpen) return null;

  const formatDuration = (ms: number) => {
    if (ms <= 0) return `${elapsedSeconds}s`;
    const sec = Math.floor(ms / 1000);
    if (sec < 60) return `${sec}s`;
    const min = Math.floor(sec / 60);
    const remSec = sec % 60;
    return `${min}m ${remSec}s`;
  };

  return (
    <div className="modal-overlay">
      <div
        className="modal-content admin-modal-content"
        style={{
          padding: '1.75rem',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.25rem',
          height: 'auto',
          maxHeight: '90vh',
          background: 'var(--panel-bg)',
          borderColor: 'var(--panel-border)',
        }}
      >
        {/* Header */}
        <div className="modal-header" style={{ padding: '0 0 1rem 0', borderBottom: '1px solid var(--panel-border)' }}>
          <div>
            <h3 className="modal-title" style={{ margin: 0 }}>
              {status === 'syncing' && 'Synchronizing Google Drive'}
              {status === 'complete' && 'Sync Completed Successfully'}
              {status === 'error' && 'Sync Encountered an Error'}
            </h3>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              Space: <strong style={{ color: 'var(--text-primary)' }}>{spaceName}</strong>
              {targetFolderName && targetFolderName !== spaceName && (
                <> &bull; Folder: <strong style={{ color: 'var(--primary)' }}>{targetFolderName}</strong></>
              )}
            </span>
          </div>
          {status !== 'syncing' && (
            <button
              onClick={onClose}
              className="modal-close"
              style={{ display: 'inline-flex' }}
            >
              <X size={20} />
            </button>
          )}
        </div>

        {/* Syncing Active View */}
        {status === 'syncing' && (
          <>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                backgroundColor: 'var(--primary-glow)',
                border: '1px solid rgba(37, 99, 235, 0.25)',
                padding: '1rem',
                borderRadius: '12px',
              }}
            >
              <div
                style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <RefreshCw
                  size={24}
                  style={{
                    color: 'var(--primary)',
                    animation: 'spin 1.2s linear infinite',
                  }}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--primary)', fontWeight: 600 }}>
                  Scanning Drive Path
                </span>
                <div
                  style={{
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    color: 'var(--text-primary)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  title={currentFolderName}
                >
                  📂 {currentFolderName}
                </div>
              </div>
            </div>

            {/* Live Metrics Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
              <div style={{ backgroundColor: 'var(--secondary)', padding: '0.75rem', borderRadius: '10px', textAlign: 'center', border: '1px solid var(--panel-border)' }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem' }}>
                  <FileText size={12} /> Items Synced
                </div>
                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)', marginTop: '0.2rem' }}>
                  {itemsSynced}
                </div>
              </div>

              <div style={{ backgroundColor: 'var(--secondary)', padding: '0.75rem', borderRadius: '10px', textAlign: 'center', border: '1px solid var(--panel-border)' }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem' }}>
                  <Folder size={12} /> Folders
                </div>
                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)', marginTop: '0.2rem' }}>
                  {foldersTraversed}
                </div>
              </div>

              <div style={{ backgroundColor: 'var(--secondary)', padding: '0.75rem', borderRadius: '10px', textAlign: 'center', border: '1px solid var(--panel-border)' }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem' }}>
                  <Clock size={12} /> Elapsed
                </div>
                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--primary)', marginTop: '0.2rem' }}>
                  {elapsedSeconds}s
                </div>
              </div>
            </div>
          </>
        )}

        {/* Complete View */}
        {status === 'complete' && (
          <>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                backgroundColor: 'rgba(34, 197, 94, 0.08)',
                border: '1px solid rgba(34, 197, 94, 0.25)',
                padding: '1rem',
                borderRadius: '12px',
              }}
            >
              <CheckCircle2 size={32} style={{ color: '#22c55e' }} />
              <div>
                <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                  Synchronization Completed
                </div>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                  Drive files & metadata updated into PostgreSQL index cleanly.
                </span>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
              <div style={{ backgroundColor: 'var(--secondary)', padding: '0.75rem', borderRadius: '10px', textAlign: 'center', border: '1px solid var(--panel-border)' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Items Synced</span>
                <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#22c55e', marginTop: '0.15rem' }}>
                  {itemsSynced}
                </div>
              </div>

              <div style={{ backgroundColor: 'var(--secondary)', padding: '0.75rem', borderRadius: '10px', textAlign: 'center', border: '1px solid var(--panel-border)' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Stale Cleaned</span>
                <div style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)', marginTop: '0.15rem' }}>
                  {deletedStaleRows}
                </div>
              </div>

              <div style={{ backgroundColor: 'var(--secondary)', padding: '0.75rem', borderRadius: '10px', textAlign: 'center', border: '1px solid var(--panel-border)' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Total Duration</span>
                <div style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--primary)', marginTop: '0.15rem' }}>
                  {formatDuration(durationMs)}
                </div>
              </div>
            </div>

            <button
              onClick={() => {
                onClose();
                onSyncComplete();
              }}
              className="btn btn-primary"
              style={{
                width: '100%',
                padding: '0.75rem',
                fontSize: '0.95rem',
                fontWeight: 600,
                borderRadius: '10px',
              }}
            >
              Close & View Updated Folder
            </button>
          </>
        )}

        {/* Error View */}
        {status === 'error' && (
          <>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                backgroundColor: 'rgba(239, 68, 68, 0.08)',
                border: '1px solid rgba(239, 68, 68, 0.25)',
                padding: '1rem',
                borderRadius: '12px',
              }}
            >
              <AlertCircle size={32} style={{ color: '#ef4444' }} />
              <div>
                <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                  Sync Interrupted
                </div>
                <span style={{ fontSize: '0.85rem', color: '#b91c1c' }}>
                  {errorMsg || 'An unexpected error occurred during Drive traversal.'}
                </span>
              </div>
            </div>

            <button
              onClick={onClose}
              className="btn btn-secondary"
              style={{
                width: '100%',
                padding: '0.75rem',
                fontSize: '0.95rem',
                fontWeight: 600,
                borderRadius: '10px',
              }}
            >
              Close
            </button>
          </>
        )}
      </div>
    </div>
  );
};
